import { parseThemeColors } from '../gameTemplates';

export const trueFalseTemplate = (data) => {
  const { title, questions, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [gameState, setGameState] = useState("start");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const traceId = useRef(null);

  const questions = ${JSON.stringify(questions, null, 2)};

  const startGame = () => {
    traceId.current = crypto.randomUUID();
    const currentStartTime = Date.now();
    setStartTime(currentStartTime);
    setGameState("playing");
    
    trackEvent('interactive_start', traceId.current, {
      startTime: currentStartTime,
      status: 'started'
    });
    
    window.parent.postMessage({
      type: 'interactive_start',
      data: {
        traceId: traceId.current,
        startTime: currentStartTime,
        status: 'started',
        url: window.location.href
      }
    }, '*');
  };

  const handleAnswer = (answer) => {
    const currentQ = questions[currentQuestion];
    const isCorrect = answer === currentQ.correctAnswer;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestion] = {
      id: currentQuestion,
      question: currentQ.question,
      userAnswer: answer,
      correctAnswer: currentQ.correctAnswer,
      isCorrect: isCorrect,
      explanation: currentQ.explanation
    };
    setUserAnswers(newAnswers);
    
    if (isCorrect) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
      setShowFeedback(false);
      setSelectedAnswer(null);
    } else {
      endGame();
    }
  };

  const endGame = () => {
    const currentEndTime = Date.now();
    const timeTaken = Math.round((currentEndTime - startTime) / 1000);
    
    setEndTime(currentEndTime);
    setGameState("end");
    
    trackEvent('interactive_end', traceId.current, {
      endTime: currentEndTime,
      timeTaken: timeTaken,
      totalQuestions: questions.length,
      totalScore: questions.length,
      userScore: score,
      status: 'completed'
    });
    
    window.parent.postMessage({
      type: 'interactive_end',
      data: {
        traceId: traceId.current,
        endTime: currentEndTime,
        timeTaken: timeTaken,
        totalQuestions: questions.length,
        totalScore: questions.length,
        userScore: score,
        status: 'completed',
        url: window.location.href,
        questions: userAnswers,
        summary: {
          totalCorrect: score,
          totalIncorrect: questions.length - score,
          accuracy: Math.round((score / questions.length) * 100)
        }
      }
    }, '*');
  };

  const resetGame = () => {
    setGameState("start");
    setCurrentQuestion(0);
    setScore(0);
    setUserAnswers([]);
    setShowFeedback(false);
    setSelectedAnswer(null);
    setStartTime(null);
    setEndTime(null);
    traceId.current = null;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return \`\${minutes}m \${remainingSeconds}s\`;
    }
    return \`\${remainingSeconds}s\`;
  };

  return (
    <div 
      className="relative min-h-screen bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: 'url(${backgroundImage})',
        fontFamily: '${themeFonts}'
      }}
    >
      <div className="relative z-10 min-h-screen flex items-center justify-center">
        {gameState === "start" && (
          <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-10 py-24 px-6">
              <h1 className="text-[${colors.primary}] text-[32px] font-bold leading-[150%] tracking-[-0.022rem] text-center">
                ${title}
              </h1>
              <p className="text-[#4b5563] text-[24px] font-medium leading-[150%] tracking-[-0.0165rem] text-center">
                Let's play the classic true or false game.
              </p>
              <button
        onClick={startGame}
                className="w-44 sm:w-[252px] h-12 sm:h-[55px] rounded-md hover:opacity-90 focus:outline-none flex items-center justify-center"
                style={{ backgroundColor: '${colors.primary}' }}
              >
                <span className="text-white text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">
                  Begin
                </span>
              </button>
    </div>
          </div>
        )}

        {gameState === "playing" && (
          <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="w-full h-3 absolute inset-x-0 top-0 bg-zinc-200 rounded-tl-2xl rounded-tr-2xl">
              <div
                className="h-3 rounded-tl-2xl"
                style={{ 
                  width: \`\${((currentQuestion + 1) / questions.length) * 100}%\`,
                  backgroundColor: '${colors.primary}'
                }}
              />
            </div>

            <div className="flex flex-col h-full">
              {!showFeedback ? (
                <>
                  <div className="pt-16 px-14">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-[${colors.primary}] text-[32px] font-bold leading-[150%] tracking-[-0.022rem]">
                        Question {currentQuestion + 1}
                      </h2>
                      <span className="px-3 py-0.5 rounded-full text-[20px] font-semibold leading-[150%] tracking-[-0.0138rem] text-[${colors.secondary}] bg-[${colors.secondary}20]">
                        {currentQuestion + 1}/{questions.length}
                      </span>
                    </div>

                    <p className="text-[#4b5563] text-[24px] font-medium leading-[150%] tracking-[-0.0165rem]"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(questions[currentQuestion]?.question) 
                    }}
                    >
                    </p>
                  </div>

                  <div className="h-6" />

                  <div className="px-14 pb-10">
                    <div className="grid grid-cols-3 gap-4 justify-center">
                      <button
              onClick={() => handleAnswer(true)}
                        className="w-full h-14 rounded-md hover:opacity-90 text-white flex items-center justify-center focus:outline-none"
                        style={{ backgroundColor: '${colors.primary}' }}
                      >
                        <span className="text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">True</span>
                      </button>
                      <button
              onClick={() => handleAnswer(false)}
                        className="w-full h-14 rounded-md hover:opacity-90 text-white flex items-center justify-center focus:outline-none"
                        style={{ backgroundColor: '${colors.primary}' }}
                      >
                        <span className="text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">False</span>
                      </button>
                      <button
                        onClick={() => handleAnswer('skip')}
                        className="w-full h-14 rounded-md hover:opacity-90 text-[${colors.primary}] flex items-center justify-center focus:outline-none"
                        style={{ backgroundColor: '${colors.primary}20' }}
                      >
                        <span className="text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">Skip</span>
                      </button>
          </div>
                  </div>
                </>
              ) : (
                <div className="pt-16 px-14 pb-10 flex flex-col h-full">
                  <div className="flex flex-col gap-8">
                    <div className="flex items-center gap-4">
                      {(() => {
                        const lastAnswer = userAnswers[currentQuestion]?.userAnswer;
                        const correctAnswer = questions[currentQuestion].correctAnswer;
                        
                        return (
                          <>
                            <div
                              className={\`w-16 h-16 rounded-full flex items-center justify-center \${
                                lastAnswer === 'skip'
                                  ? 'bg-amber-500'
                                  : lastAnswer === correctAnswer
                                  ? 'bg-green-600'
                                  : 'bg-red-600'
                              }\`}
                            >
                              <span className="text-white text-3xl font-bold">
                                {lastAnswer === 'skip' ? '−' : lastAnswer === correctAnswer ? '✓' : '✗'}
                              </span>
                            </div>
                            <span
                              className={\`text-3xl font-bold \${
                                lastAnswer === 'skip'
                                  ? 'text-amber-500'
                                  : lastAnswer === correctAnswer
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }\`}
                            >
                              {lastAnswer === 'skip'
                                ? 'Skipped'
                                : lastAnswer === correctAnswer
                                ? 'Correct'
                                : 'Incorrect'}
                            </span>
                          </>
                        );
                      })()}
                    </div>

<p 
    className="text-[#4b5563] text-[24px] font-medium leading-[150%] tracking-[-0.0165rem] text-left"
    dangerouslySetInnerHTML={{ 
        __html: DOMPurify.sanitize(questions[currentQuestion]?.explanation) 
    }}
/>
                    <div className="flex justify-start">
                      <button
                        onClick={handleNext}
                        className="w-full max-w-[250px] h-14 rounded-md hover:opacity-90 text-white flex items-center justify-center focus:outline-none"
              style={{ backgroundColor: '${colors.primary}' }}
            >
                        <span className="text-[24px] font-semibold leading-[150%] tracking-[-0.0165rem]">
                          {currentQuestion === questions.length - 1 ? 'Show Summary' : 'Next'}
                        </span>
                      </button>
                    </div>
                  </div>
          </div>
        )}
      </div>
    </div>
        )}

        {gameState === "end" && (() => {
          const totalCorrect = userAnswers.filter(a => a.isCorrect).length;
          const scorePercentage = questions.length ? Math.round((totalCorrect / questions.length) * 100) : 0;
          const answered = userAnswers.filter(a => a.userAnswer !== 'skip' && a.userAnswer !== null).length;
          const totalTime = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;

          return (
            <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
              <div className="p-10">
                <h2 className="text-[${colors.primary}] text-[32px] font-bold tracking-[-0.022rem] text-center">
                  Your Results
                </h2>
                <hr className="my-6 w-1/2 mx-auto border-t-[#E0DEEF]" />

                <div className="px-1.5 sm:px-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs mb-1 tracking-wide">Final Score</div>
                      <div className="text-[${colors.primary}] text-3xl font-bold">{scorePercentage}%</div>
                    </div>
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs mb-1 tracking-wide">Total Time</div>
                      <div className="text-[${colors.primary}] text-3xl font-bold">{formatTime(totalTime)}</div>
                    </div>
                    <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[#515151] text-xs mb-1 tracking-wide">Answered</div>
                      <div className="text-[${colors.primary}] text-3xl font-bold">
                        {answered}/{questions.length}
                      </div>
                    </div>
      </div>
      
                  <div className="space-y-4 mb-10">
                    {questions.map((q, idx) => {
                      const ua = userAnswers[idx];
                      const isCorrect = ua?.isCorrect || false;
                      const isSkipped = ua?.userAnswer === 'skip';

                      const bgTint = isSkipped
                        ? 'bg-yellow-50'
                        : isCorrect
                        ? 'bg-green-50'
                        : 'bg-red-50';

                      const icon = isSkipped ? '−' : isCorrect ? '✓' : '✗';

                      const ans = isSkipped
                        ? \`Skipped | Correct: \${q.correctAnswer ? 'True' : 'False'}\`
                        : isCorrect
                        ? \`Your answer: \${ua.userAnswer ? 'True' : 'False'}\`
                        : \`Your answer: \${ua.userAnswer ? 'True' : 'False'} | Correct: \${
                            q.correctAnswer ? 'True' : 'False'
                          }\`;

                      return (
                        <div
                          key={idx}
                          className={\`\${bgTint} border border-[#E0DEEF] p-4 rounded-2xl flex gap-4\`}
                        >
                          <div className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center"
                               style={{ backgroundColor: '${colors.primary}' }}>
                            {icon}
                          </div>
                          <div className="flex-1">
                            <p className="text-[${colors.primary}] text-lg font-medium leading-snug mb-1">
                              Q{idx + 1}: <span dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(q.question) 
                              }}></span>
                            </p>
                            <p className="text-[#4e4e4e] text-md">{ans}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
        onClick={resetGame}
                    className="w-full max-w-[200px] h-14 hover:opacity-90 rounded-md text-white text-[24px] font-semibold"
        style={{ backgroundColor: '${colors.primary}' }}
      >
                    Restart Quiz
                  </button>
                </div>
              </div>
    </div>
  );
        })()}
      </div>
    </div>
  );
}`;
};
